import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { sbq, throwOnError } from '../../shared/utils/supabaseQuery';

type Jurisdiction = { id: number; csp_id: string; title: string };
type Subject = { subject: string };
type RootSet = { id: number; title: string; child_count: number };
export type TreeChild = { id: number; description: string; indexed: boolean };
type TreeParent = TreeChild & { children: TreeChild[] };
type TreePayload = {
  root: { id: number; title: string };
  parents: TreeParent[];
};

export const standardsApi = createApi({
  reducerPath: 'standardsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Jurisdiction', 'Subject', 'Root', 'Tree'],
  endpoints: (builder) => ({
    // ───────────────────────── jurisdictions
    getJurisdictions: builder.query<Jurisdiction[], void>({
      queryFn: sbq(async (sb) => {
        const rows = throwOnError(
          await sb.from('jurisdictions').select('id, title').order('title')
        );
        return rows as Jurisdiction[];
      }, 'Jurisdictions'),
      providesTags: ['Jurisdiction'],
    }),

    // ───────────────────────── subjects for a jurisdiction
    getSubjects: builder.query<Subject[], number>({
      queryFn: sbq(async (sb, jurisdictionId) => {
        const rows = throwOnError(
          await sb
            .from('standards_warehouse')
            .select('subject')
            .eq('jurisdiction_id', jurisdictionId)
            .eq('parent_ids', '{}')
            .order('subject')
        );
        // distinct via JS (PostgREST doesn't support DISTINCT on individual columns easily)
        const seen = new Set<string>();
        return (rows as any[]).reduce<Subject[]>((acc, r) => {
          if (r.subject && !seen.has(r.subject)) {
            seen.add(r.subject);
            acc.push({ subject: r.subject });
          }
          return acc;
        }, []);
      }, 'Subjects'),
      providesTags: ['Subject'],
    }),

    // ───────────────────────── root standard‑sets for a subject
    getRoots: builder.query<RootSet[], { jurisdictionId: number; subject: string }>({
      queryFn: sbq(async (sb, { jurisdictionId, subject }) => {
        const rows = throwOnError(
          await sb
            .from('standards_warehouse')
            .select('id, title, child_count')
            .eq('jurisdiction_id', jurisdictionId)
            .eq('subject', subject)
            .eq('parent_ids', '{}') // root rows have empty array
            .order('title')
        );
        return rows as RootSet[];
      }, 'Roots'),
      providesTags: ['Root'],
    }),

    // ───────────────────────── full tree for a given root
    getTree: builder.query<TreePayload, number>({
      queryFn: sbq(async (sb, rootId) => {
        // fetch root
        const root = throwOnError(
          await sb.from('standards_warehouse').select('id, title').eq('id', rootId).single()
        ) as any;

        // fetch parents (depth 0)
        const parents = throwOnError(
          await sb
            .from('standards_warehouse')
            .select('id, document, indexed')
            .contains('parent_ids', [rootId])
            .filter('document->>depth', 'eq', '0')
            .order('document->>position', { ascending: true })
        ) as any[];

        // Batch-fetch all children (depth 1) for all parents in one query
        const parentIds = parents.map((p: any) => p.id as number);
        let allChildren: any[] = [];
        if (parentIds.length > 0) {
          try {
            allChildren = throwOnError(
              await sb
                .from('standards_warehouse')
                .select('id, document, indexed, parent_ids')
                .overlaps('parent_ids', parentIds)
                .filter('document->>depth', 'eq', '1')
                .order('document->>position', { ascending: true })
            ) as any[];
          } catch (err) {
            console.error('Failed to batch-fetch children:', err);
            // Graceful degradation - parents will show with empty children
          }
        }

        // Group children by their direct parent
        const childrenByParent = new Map<number, any[]>();
        for (const c of allChildren) {
          const directParent = (c.parent_ids as number[]).find((pid: number) =>
            parentIds.includes(pid)
          );
          if (directParent != null) {
            if (!childrenByParent.has(directParent)) childrenByParent.set(directParent, []);
            childrenByParent.get(directParent)!.push(c);
          }
        }

        const fullParents: TreeParent[] = parents.map((p: any) => ({
          id: p.id,
          description: p.document.description,
          indexed: p.indexed,
          children: (childrenByParent.get(p.id) ?? []).map((c: any) => ({
            id: c.id,
            description: c.document.description,
            indexed: c.indexed,
          })),
        }));

        return {
          root: { id: root.id, title: root.title },
          parents: fullParents,
        } as TreePayload;
      }, 'Tree'),
      providesTags: (_r, _e, id) => [{ type: 'Tree', id }],
    }),
  }),
});

export const {
  useGetJurisdictionsQuery,
  useGetSubjectsQuery,
  useGetRootsQuery,
  useGetTreeQuery,
  useLazyGetSubjectsQuery,
  useLazyGetRootsQuery,
  useLazyGetTreeQuery,
} = standardsApi;
